/**
 * HostManagementTabs (Iter 9.47.r)
 *
 * Re-exports the Pipeline tab and provides the generic List tab for
 * Pilots / Subscribers / Waitlist buckets.
 */
import { useEffect, useState } from "react";
import { Loader2, Users, Clock, Mail, Inbox } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
export { HostManagementPipelineTab } from "./HostManagementPipelineTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------- Generic list tab (Pilots / Subscribers / Waitlist) ----------------

export function HostManagementListTab({ event, bucket, emptyHint }) {
  const category = event?.category || event?.pilot_category;
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!category) return;
    let alive = true;
    (async () => {
      const token = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${token}` };
      let url;
      if (bucket === "waitlist") {
        url = `${API}/waitlist/list?category=${category}`;
      } else if (bucket === "pilots") {
        url = `${API}/admin/host-applications?category=${category}&pilot=true`;
      } else {
        url = `${API}/admin/host-applications?category=${category}&status=active`;
      }
      try {
        const r = await fetch(url, { headers });
        const d = r.ok ? await r.json() : {};
        if (alive) setItems(d.entries || d.applications || []);
      } catch {
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, [category, bucket]);

  if (items === null) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 text-sm" data-testid={`${bucket}-loading`}>
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-testid={`${bucket}-empty`} className="text-center py-16 px-4">
        <Inbox className="w-10 h-10 mx-auto text-gray-300 mb-3" />
        <h3 className="text-sm font-medium text-gray-700 mb-1">Nothing here yet</h3>
        <p className="text-xs text-gray-500 max-w-sm mx-auto">{emptyHint}</p>
      </div>
    );
  }

  return (
    <div data-testid={`host-management-${bucket}`} className="space-y-2">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900 capitalize">{bucket}</h2>
        <span className="text-xs text-gray-500">{items.length} total</span>
      </div>
      <div className="space-y-1.5">
        {items.map((it, idx) => {
          const id = it.id || it._id || it.user_id || it.application_id || it.email || `idx-${idx}`;
          const name = it.brand_name || it.name || it.first_name || it.email || "Unknown";
          const meta = [it.city, it.email, it.phone].filter(Boolean).join(" · ");
          return (
            <Card key={id} data-testid={`${bucket}-row-${id}`} className="hover:shadow-sm transition">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  {bucket === "waitlist" ? <Clock className="w-4 h-4 text-gray-500" /> :
                   bucket === "pilots" ? <Users className="w-4 h-4 text-blue-600" /> :
                   <Mail className="w-4 h-4 text-purple-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                  {meta && <p className="text-[10px] text-gray-500 truncate">{meta}</p>}
                </div>
                <span className="text-[10px] text-gray-400 capitalize">{bucket}</span>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
